// ============================================================================
// deviceFingerprint.js — a stable per-device/browser identifier for the public
// SIR search, used by the server-side free-search meter (sirQuota.js).
//
// WHAT THIS IS NOT
// It is not an IMEI, a MAC address or a hardware serial. No browser exposes
// those to a web page, on any platform — a website simply cannot read them.
// Anyone promising otherwise is describing a native app.
//
// WHAT IT IS
// A hash of signals the browser DOES expose and that a normal user never
// changes: user agent, platform, screen geometry, timezone, CPU/memory class,
// and how this particular GPU + font stack renders a fixed canvas and a WebGL
// probe. That combination is:
//
//   ✓ stable across page loads, restarts and reboots
//   ✓ SURVIVES clearing localStorage/cookies  ← the point; a stored UUID does not
//   ✓ survives private / incognito windows on the same browser
//   ✗ different in a different browser on the same machine
//   ✗ defeatable by a spoofed user agent or an anti-fingerprinting browser
//
// The last two are why the free tier is only 5 searches and why the server ALSO
// meters by IP: the fingerprint raises the cost of farming free searches, and
// the OTP wall — a real phone number that receives a real WhatsApp message — is
// what actually stops it.
// ============================================================================

/** Cached for the page's lifetime; the inputs cannot change mid-session. */
let cached = null;
let inFlight = null;

/**
 * Draws text and shapes whose rasterisation depends on the GPU, the driver and
 * the installed fonts. Wrapped because a blocked canvas (privacy extensions,
 * some enterprise policies) throws rather than returning blank.
 */
function canvasSignal() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "nocanvas";

    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(10, 5, 80, 20);
    ctx.fillStyle = "#069";
    // Devanagari is deliberate: which Marathi font the device falls back to is
    // itself a strong signal, and this app's users are on Marathi devices.
    ctx.fillText("SmartSearch मतदार 0123", 12, 8);
    ctx.strokeStyle = "rgba(0,80,160,0.7)";
    ctx.arc(60, 40, 18, 0, Math.PI * 2);
    ctx.stroke();

    return canvas.toDataURL().slice(-160);
  } catch {
    return "canvaserr";
  }
}

/** GPU vendor/renderer strings — very stable, and coarse enough not to be PII. */
function webglSignal() {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "nowebgl";
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (!dbg) return String(gl.getParameter(gl.VERSION) || "webgl");
    return [
      gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL),
      gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL),
    ].join("~");
  } catch {
    return "webglerr";
  }
}

/** Collects the raw signal string. Exported for the "why am I blocked" panel. */
export function collectSignals() {
  const nav = typeof navigator === "undefined" ? {} : navigator;
  const scr = (typeof window !== "undefined" && window.screen) || {};

  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    timezone = String(new Date().getTimezoneOffset());
  }

  return [
    nav.userAgent || "",
    nav.platform || "",
    // Full list, not just the first: the ORDER of preferred languages is set
    // by the user and rarely changes.
    (nav.languages || [nav.language || ""]).join(","),
    timezone,
    // Screen size is per-device. devicePixelRatio separates a phone from a
    // desktop reporting the same CSS pixel size.
    [scr.width, scr.height, scr.colorDepth, window.devicePixelRatio].join("x"),
    String(nav.hardwareConcurrency || ""),
    String(nav.deviceMemory || ""),
    String(nav.maxTouchPoints || ""),
    canvasSignal(),
    webglSignal(),
  ].join("|");
}

/**
 * SHA-256 via WebCrypto. Unavailable on plain http:// origins other than
 * localhost, so a deterministic fallback keeps the meter working there rather
 * than leaving the device unidentifiable (which the server refuses to serve).
 */
async function hashHex(text) {
  try {
    if (window.crypto && window.crypto.subtle) {
      const bytes = new TextEncoder().encode(text);
      const digest = await window.crypto.subtle.digest("SHA-256", bytes);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }
  } catch {
    // fall through
  }

  // FNV-1a over four offset passes. Weaker than SHA-256 and only used on
  // insecure origins, but stable and collision-resistant enough for a meter.
  let out = "";
  for (let pass = 0; pass < 4; pass++) {
    let h = 0x811c9dc5 ^ pass;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    out += h.toString(16).padStart(8, "0");
  }
  return out.padEnd(64, "0").slice(0, 64);
}

/** The 64-char hex device hash. Resolves once and is reused thereafter. */
export function getDeviceHash() {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = hashHex(collectSignals())
    .then((hash) => {
      cached = hash;
      inFlight = null;
      return hash;
    })
    .catch(() => {
      inFlight = null;
      return "";
    });

  return inFlight;
}

/** Coarse device description, stored alongside the hash for the admin view. */
export function getDeviceMeta() {
  const nav = typeof navigator === "undefined" ? {} : navigator;
  const scr = (typeof window !== "undefined" && window.screen) || {};
  return {
    Platform: String(nav.platform || nav.userAgentData?.platform || "").slice(0, 60),
    Screen: [scr.width, scr.height].join("x"),
  };
}
